export interface MenuItem {
  title: string;
  href: string;
  description?: string;
}

export interface MenuSection {
  title: string;
  items: MenuItem[];
}

export interface MegaMenuData {
  title: string;
  sections: MenuSection[];
}

export interface PublicRoute {
  path: string;
  name: string;
  icon?: string;
  colors?: {
    primary: string;
    secondary: string;
    gradient?: {
      base: string;
      shimmer: string;
    } | null;
    button: {
      primary: string;
      secondary: string;
    };
    nav: {
      active: string;
      hover: string;
    };
  };
}

// Centralized mega menu data
export const megaMenuData: MegaMenuData[] = [
  {
    title: "Services",
    sections: [
      {
        title: "Core Services",
        items: [
          {
            title: "Premium Linen Supply",
            href: "/linen-services",
            description: "Hotel-grade bed linens and towels",
          },
          {
            title: "Cleaning Services",
            href: "/cleaning-services",
            description: "Professional cleaning solutions",
          },
          {
            title: "Delivery & Installation",
            href: "/delivery",
            description: "Scheduled delivery and setup",
          },
          {
            title: "Property Support",
            href: "/property-support",
            description: "24/7 management support",
          },
          {
            title: "Cleaning & Guest Packs",
            href: "/cleaning-guest-solutions",
            description: "Curated amenities and guest-ready extras",
          },
          {
            title: "Maintenance",
            href: "/maintenance",
            description: "Rapid repairs and preventative care",
          },
        ],
      },
    ],
  },
  {
    title: "Solutions",
    sections: [
      {
        title: "Solutions Overview",
        items: [
          {
            title: "Solutions Overview",
            href: "/solutions",
            description: "Compare all solution tiers",
          },
          {
            title: "Airbnb Hosts",
            href: "/solutions/airbnb-hosts",
            description: "Individual property managers",
          },
          {
            title: "Property Companies",
            href: "/solutions/property-companies",
            description: "Professional management firms",
          },
          {
            title: "Short-term Rentals",
            href: "/solutions/short-term-rentals",
            description: "Rental operators scaling across cities",
          },
          {
            title: "Serviced Accommodation",
            href: "/solutions/serviced-accommodation",
            description: "Corporate stay specialists",
          },
          {
            title: "Student PBSA",
            href: "/solutions/student-pbsa",
            description: "Purpose-built student accommodation",
          },
          {
            title: "Hospitality",
            href: "/solutions/hospitality",
            description: "Hotels and hospitality groups",
          },
        ],
      },
    ],
  },
  {
    title: "Company",
    sections: [
      {
        title: "About Us",
        items: [
          { title: "Our Story", href: "/about", description: "How we started" },
          {
            title: "Our Team",
            href: "/about/team",
            description: "Meet the experts",
          },
          {
            title: "Our Values",
            href: "/about/values",
            description: "What drives us",
          },
          { title: "Careers", href: "/about/careers", description: "Join our team" },
        ],
      },
      {
        title: "Resources",
        items: [
          {
            title: "Help Center",
            href: "/help-center",
            description: "Guides, FAQs & support",
          },
          // HIDDEN ROUTE - Uncomment to show in navigation
          // {
          //   title: "Integrations",
          //   href: "/documentation/integrations",
          //   description: "Connect Replenico with your stack",
          // },
          // HIDDEN ROUTE - Uncomment to show in navigation
          // {
          //   title: "API Access",
          //   href: "/documentation/api-access",
          //   description: "Developer tooling and credentials",
          // },
          { title: "Blog", href: "/blog", description: "Latest insights" },
        ],
      },
    ],
  },
];

// Public routes configuration with centralized colors
export const publicRoutes: PublicRoute[] = [
  {
    path: "/services",
    name: "Services",
    icon: "/assets/REPLENICO/page-icons/services.png",
    colors: {
      primary: "blue-600",
      secondary: "blue-700",
      gradient: {
        base: "from-blue-400 via-cyan-400 via-teal-400 via-emerald-400 to-blue-500",
        shimmer:
          "from-blue-300/60 via-cyan-300/50 via-teal-300/60 to-blue-400/50",
      },
      button: {
        primary: "bg-blue-600 hover:bg-blue-700 text-white",
        secondary:
          "text-blue-600 hover:text-blue-700 border-blue-600 hover:border-blue-700",
      },
      nav: {
        active: "text-blue-600",
        hover: "text-blue-500 hover:text-blue-700",
      },
    },
  },
  {
    path: "/about",
    name: "About",
    icon: "/assets/REPLENICO/page-icons/compass.png",
    colors: {
      primary: "purple-600",
      secondary: "purple-700",
      gradient: {
        base: "from-purple-400 via-violet-400 via-fuchsia-400 via-pink-400 to-purple-500",
        shimmer:
          "from-purple-300/60 via-violet-300/50 via-fuchsia-300/60 to-purple-400/50",
      },
      button: {
        primary: "bg-purple-600 hover:bg-purple-700 text-white",
        secondary:
          "text-purple-600 hover:text-purple-700 border-purple-600 hover:border-purple-700",
      },
      nav: {
        active: "text-purple-600",
        hover: "text-purple-500 hover:text-purple-700",
      },
    },
  },
  {
    path: "/contact",
    name: "Contact",
    icon: "/assets/REPLENICO/page-icons/contact.png",
    colors: {
      primary: "purple-600",
      secondary: "purple-700",
      gradient: {
        base: "from-purple-400 via-violet-400 via-fuchsia-400 via-pink-400 to-purple-500",
        shimmer:
          "from-purple-300/60 via-violet-300/50 via-fuchsia-300/60 to-purple-400/50",
      },
      button: {
        primary: "bg-purple-600 hover:bg-purple-700 text-white",
        secondary:
          "text-purple-600 hover:text-purple-700 border-purple-600 hover:border-purple-700",
      },
      nav: {
        active: "text-purple-600",
        hover: "text-purple-500 hover:text-purple-700",
      },
    },
  },
  {
    path: "/book-a-demo",
    name: "Book a Demo",
    icon: "/assets/REPLENICO/page-icons/contact.png",
    colors: {
      primary: "purple-600",
      secondary: "purple-700",
      gradient: {
        base: "from-emerald-400 via-blue-400 via-indigo-400 via-violet-400 to-purple-500",
        shimmer:
          "from-emerald-300/60 via-blue-300/50 via-indigo-300/60 to-purple-400/50",
      },
      button: {
        primary: "bg-purple-600 hover:bg-purple-700 text-white",
        secondary:
          "text-purple-600 hover:text-purple-700 border-purple-600 hover:border-purple-700",
      },
      nav: {
        active: "text-purple-600",
        hover: "text-purple-500 hover:text-purple-700",
      },
    },
  },
  {
    path: "/help-center",
    name: "Help Center",
    icon: "/assets/REPLENICO/page-icons/compass.png",
    colors: {
      primary: "purple-600",
      secondary: "purple-700",
      gradient: {
        base: "from-purple-400 via-violet-400 via-fuchsia-400 via-pink-400 to-purple-500",
        shimmer:
          "from-purple-300/60 via-violet-300/50 via-fuchsia-300/60 to-purple-400/50",
      },
      button: {
        primary: "bg-purple-600 hover:bg-purple-700 text-white",
        secondary:
          "text-purple-600 hover:text-purple-700 border-purple-600 hover:border-purple-700",
      },
      nav: {
        active: "text-purple-600",
        hover: "text-purple-500 hover:text-purple-700",
      },
    },
  },
  // HIDDEN ROUTE - Uncomment to show in navigation
  // {
  //   path: "/pricing",
  //   name: "Pricing",
  //   icon: "/assets/REPLENICO/page-icons/pricing.png",
  //   colors: {
  //     primary: "purple-600",
  //     secondary: "purple-700",
  //     gradient: {
  //       base: "from-purple-400 via-violet-400 via-fuchsia-400 to-purple-500",
  //       shimmer:
  //         "from-purple-300/60 via-violet-300/50 via-fuchsia-300/60 to-purple-400/50",
  //     },
  //     button: {
  //       primary: "bg-purple-600 hover:bg-purple-700 text-white",
  //       secondary:
  //         "text-purple-600 hover:text-purple-700 border-purple-600 hover:border-purple-700",
  //     },
  //     nav: {
  //       active: "text-purple-600",
  //       hover: "text-purple-500 hover:text-purple-700",
  //     },
  //   },
  // },
  {
    path: "/solutions/airbnb-hosts",
    name: "Airbnb Hosts",
    icon: "/assets/REPLENICO/page-icons/solutions.png",
    colors: {
      primary: "emerald-600",
      secondary: "emerald-700",
      gradient: {
        base: "from-emerald-400 via-green-400 via-teal-400 via-slate-400 to-emerald-500",
        shimmer:
          "from-emerald-300/60 via-green-300/50 via-teal-300/60 via-slate-300/50 to-emerald-400/60",
      },
      button: {
        primary: "bg-emerald-600 hover:bg-emerald-700 text-white",
        secondary:
          "text-emerald-600 hover:text-emerald-700 border-emerald-600 hover:border-emerald-700",
      },
      nav: {
        active: "text-emerald-600",
        hover: "text-emerald-500 hover:text-emerald-700",
      },
    },
  },
  {
    path: "/solutions/property-companies",
    name: "Property Companies",
    icon: "/assets/REPLENICO/page-icons/solutions.png",
    colors: {
      primary: "emerald-600",
      secondary: "emerald-700",
      gradient: {
        base: "from-emerald-400 via-green-400 via-teal-400 via-slate-400 to-emerald-500",
        shimmer:
          "from-emerald-300/60 via-green-300/50 via-teal-300/60 via-slate-300/50 to-emerald-400/60",
      },
      button: {
        primary: "bg-emerald-600 hover:bg-emerald-700 text-white",
        secondary:
          "text-emerald-600 hover:text-emerald-700 border-emerald-600 hover:border-emerald-700",
      },
      nav: {
        active: "text-emerald-600",
        hover: "text-emerald-500 hover:text-emerald-700",
      },
    },
  },
  {
    path: "/linen-services",
    name: "Premium Linen Supply",
    icon: "/assets/REPLENICO/page-icons/services.png",
    colors: {
      primary: "blue-600",
      secondary: "blue-700",
      gradient: {
        base: "from-blue-400 via-cyan-400 via-teal-400 via-emerald-400 to-blue-500",
        shimmer:
          "from-blue-300/60 via-cyan-300/50 via-teal-300/60 to-blue-400/50",
      },
      button: {
        primary: "bg-blue-600 hover:bg-blue-700 text-white",
        secondary:
          "text-blue-600 hover:text-blue-700 border-blue-600 hover:border-blue-700",
      },
      nav: {
        active: "text-blue-600",
        hover: "text-blue-500 hover:text-blue-700",
      },
    },
  },
  {
    path: "/cleaning-services",
    name: "Cleaning Services",
    icon: "/assets/REPLENICO/page-icons/services.png",
    colors: {
      primary: "blue-600",
      secondary: "blue-700",
      gradient: {
        base: "from-blue-400 via-cyan-400 via-teal-400 via-emerald-400 to-blue-500",
        shimmer:
          "from-blue-300/60 via-cyan-300/50 via-teal-300/60 to-blue-400/50",
      },
      button: {
        primary: "bg-blue-600 hover:bg-blue-700 text-white",
        secondary:
          "text-blue-600 hover:text-blue-700 border-blue-600 hover:border-blue-700",
      },
      nav: {
        active: "text-blue-600",
        hover: "text-blue-500 hover:text-blue-700",
      },
    },
  },
  {
    path: "/cleaning-guest-solutions",
    name: "Cleaning & Guest Packs",
    icon: "/assets/REPLENICO/page-icons/services.png",
    colors: {
      primary: "blue-600",
      secondary: "blue-700",
      gradient: {
        base: "from-blue-400 via-cyan-400 via-teal-400 via-emerald-400 to-blue-500",
        shimmer:
          "from-blue-300/60 via-cyan-300/50 via-teal-300/60 to-blue-400/50",
      },
      button: {
        primary: "bg-blue-600 hover:bg-blue-700 text-white",
        secondary:
          "text-blue-600 hover:text-blue-700 border-blue-600 hover:border-blue-700",
      },
      nav: {
        active: "text-blue-600",
        hover: "text-blue-500 hover:text-blue-700",
      },
    },
  },
  {
    path: "/maintenance",
    name: "Maintenance",
    icon: "/assets/REPLENICO/page-icons/services.png",
    colors: {
      primary: "blue-600",
      secondary: "blue-700",
      gradient: {
        base: "from-blue-400 via-cyan-400 via-teal-400 via-emerald-400 to-blue-500",
        shimmer:
          "from-blue-300/60 via-cyan-300/50 via-teal-300/60 to-blue-400/50",
      },
      button: {
        primary: "bg-blue-600 hover:bg-blue-700 text-white",
        secondary:
          "text-blue-600 hover:text-blue-700 border-blue-600 hover:border-blue-700",
      },
      nav: {
        active: "text-blue-600",
        hover: "text-blue-500 hover:text-blue-700",
      },
    },
  },
  {
    path: "/delivery",
    name: "Delivery & Installation",
    icon: "/assets/REPLENICO/page-icons/services.png",
    colors: {
      primary: "blue-600",
      secondary: "blue-700",
      gradient: {
        base: "from-blue-400 via-cyan-400 via-teal-400 via-emerald-400 to-blue-500",
        shimmer:
          "from-blue-300/60 via-cyan-300/50 via-teal-300/60 to-blue-400/50",
      },
      button: {
        primary: "bg-blue-600 hover:bg-blue-700 text-white",
        secondary:
          "text-blue-600 hover:text-blue-700 border-blue-600 hover:border-blue-700",
      },
      nav: {
        active: "text-blue-600",
        hover: "text-blue-500 hover:text-blue-700",
      },
    },
  },
  {
    path: "/property-support",
    name: "Property Support",
    icon: "/assets/REPLENICO/page-icons/services.png",
    colors: {
      primary: "blue-600",
      secondary: "blue-700",
      gradient: {
        base: "from-blue-400 via-cyan-400 via-teal-400 via-emerald-400 to-blue-500",
        shimmer:
          "from-blue-300/60 via-cyan-300/50 via-teal-300/60 to-blue-400/50",
      },
      button: {
        primary: "bg-blue-600 hover:bg-blue-700 text-white",
        secondary:
          "text-blue-600 hover:text-blue-700 border-blue-600 hover:border-blue-700",
      },
      nav: {
        active: "text-blue-600",
        hover: "text-blue-500 hover:text-blue-700",
      },
    },
  },
  {
    path: "/solutions/short-term-rentals",
    name: "Short-term Rentals",
    icon: "/assets/REPLENICO/page-icons/solutions.png",
    colors: {
      primary: "emerald-600",
      secondary: "emerald-700",
      gradient: {
        base: "from-emerald-400 via-green-400 via-teal-400 via-slate-400 to-emerald-500",
        shimmer:
          "from-emerald-300/60 via-green-300/50 via-teal-300/60 via-slate-300/50 to-emerald-400/60",
      },
      button: {
        primary: "bg-emerald-600 hover:bg-emerald-700 text-white",
        secondary:
          "text-emerald-600 hover:text-emerald-700 border-emerald-600 hover:border-emerald-700",
      },
      nav: {
        active: "text-emerald-600",
        hover: "text-emerald-500 hover:text-emerald-700",
      },
    },
  },
  {
    path: "/solutions/serviced-accommodation",
    name: "Serviced Accommodation",
    icon: "/assets/REPLENICO/page-icons/solutions.png",
    colors: {
      primary: "emerald-600",
      secondary: "emerald-700",
      gradient: {
        base: "from-emerald-400 via-green-400 via-teal-400 via-slate-400 to-emerald-500",
        shimmer:
          "from-emerald-300/60 via-green-300/50 via-teal-300/60 via-slate-300/50 to-emerald-400/60",
      },
      button: {
        primary: "bg-emerald-600 hover:bg-emerald-700 text-white",
        secondary:
          "text-emerald-600 hover:text-emerald-700 border-emerald-600 hover:border-emerald-700",
      },
      nav: {
        active: "text-emerald-600",
        hover: "text-emerald-500 hover:text-emerald-700",
      },
    },
  },
  {
    path: "/solutions/student-pbsa",
    name: "Student PBSA",
    icon: "/assets/REPLENICO/page-icons/solutions.png",
    colors: {
      primary: "emerald-600",
      secondary: "emerald-700",
      gradient: {
        base: "from-emerald-400 via-green-400 via-teal-400 via-slate-400 to-emerald-500",
        shimmer:
          "from-emerald-300/60 via-green-300/50 via-teal-300/60 via-slate-300/50 to-emerald-400/60",
      },
      button: {
        primary: "bg-emerald-600 hover:bg-emerald-700 text-white",
        secondary:
          "text-emerald-600 hover:text-emerald-700 border-emerald-600 hover:border-emerald-700",
      },
      nav: {
        active: "text-emerald-600",
        hover: "text-emerald-500 hover:text-emerald-700",
      },
    },
  },
  {
    path: "/solutions/hospitality",
    name: "Hospitality",
    icon: "/assets/REPLENICO/page-icons/solutions.png",
    colors: {
      primary: "emerald-600",
      secondary: "emerald-700",
      gradient: {
        base: "from-emerald-400 via-green-400 via-teal-400 via-slate-400 to-emerald-500",
        shimmer:
          "from-emerald-300/60 via-green-300/50 via-teal-300/60 via-slate-300/50 to-emerald-400/60",
      },
      button: {
        primary: "bg-emerald-600 hover:bg-emerald-700 text-white",
        secondary:
          "text-emerald-600 hover:text-emerald-700 border-emerald-600 hover:border-emerald-700",
      },
      nav: {
        active: "text-emerald-600",
        hover: "text-emerald-500 hover:text-emerald-700",
      },
    },
  },
  {
    path: "/solutions/inventory-management",
    name: "Inventory Management",
    icon: "/assets/REPLENICO/page-icons/solutions.png",
    colors: {
      primary: "emerald-600",
      secondary: "emerald-700",
      gradient: {
        base: "from-emerald-400 via-green-400 via-teal-400 via-slate-400 to-emerald-500",
        shimmer:
          "from-emerald-300/60 via-green-300/50 via-teal-300/60 via-slate-300/50 to-emerald-400/60",
      },
      button: {
        primary: "bg-emerald-600 hover:bg-emerald-700 text-white",
        secondary:
          "text-emerald-600 hover:text-emerald-700 border-emerald-600 hover:border-emerald-700",
      },
      nav: {
        active: "text-emerald-600",
        hover: "text-emerald-500 hover:text-emerald-700",
      },
    },
  },
  {
    path: "/solutions/analytics-reporting",
    name: "Analytics & Reporting",
    icon: "/assets/REPLENICO/page-icons/solutions.png",
    colors: {
      primary: "emerald-600",
      secondary: "emerald-700",
      gradient: {
        base: "from-emerald-400 via-green-400 via-teal-400 via-slate-400 to-emerald-500",
        shimmer:
          "from-emerald-300/60 via-green-300/50 via-teal-300/60 via-slate-300/50 to-emerald-400/60",
      },
      button: {
        primary: "bg-emerald-600 hover:bg-emerald-700 text-white",
        secondary:
          "text-emerald-600 hover:text-emerald-700 border-emerald-600 hover-border-emerald-700",
      },
      nav: {
        active: "text-emerald-600",
        hover: "text-emerald-500 hover:text-emerald-700",
      },
    },
  },
  {
    path: "/solutions",
    name: "Solutions",
    icon: "/assets/REPLENICO/page-icons/solutions.png",
    colors: {
      primary: "teal-600",
      secondary: "teal-700",
      gradient: {
        base: "from-teal-400 via-cyan-400 via-emerald-400 via-green-400 to-teal-500",
        shimmer:
          "from-teal-300/60 via-cyan-300/50 via-emerald-300/60 to-teal-400/50",
      },
      button: {
        primary: "bg-teal-600 hover:bg-teal-700 text-white",
        secondary:
          "text-teal-600 hover:text-teal-700 border-teal-600 hover:border-teal-700",
      },
      nav: {
        active: "text-teal-600",
        hover: "text-teal-500 hover:text-teal-700",
      },
    },
  },
  // HIDDEN ROUTE - Uncomment to show in navigation
  // {
  //   path: "/documentation/integrations",
  //   name: "Integrations",
  //   icon: "/assets/REPLENICO/page-icons/list.png",
  //   colors: {
  //     primary: "purple-600",
  //     secondary: "purple-700",
  //     gradient: {
  //       base: "from-purple-400 via-violet-400 via-fuchsia-400 to-purple-500",
  //       shimmer:
  //         "from-purple-300/60 via-violet-300/50 via-fuchsia-300/60 to-purple-400/50",
  //     },
  //     button: {
  //       primary: "bg-purple-600 hover:bg-purple-700 text-white",
  //       secondary:
  //         "text-purple-600 hover:text-purple-700 border-purple-600 hover:border-purple-700",
  //     },
  //     nav: {
  //       active: "text-purple-600",
  //       hover: "text-purple-500 hover:text-purple-700",
  //     },
  //   },
  // },
  // HIDDEN ROUTE - Uncomment to show in navigation
  // {
  //   path: "/documentation/api-access",
  //   name: "API Access",
  //   icon: "/assets/REPLENICO/page-icons/list.png",
  //   colors: {
  //     primary: "purple-600",
  //     secondary: "purple-700",
  //     gradient: {
  //       base: "from-purple-400 via-violet-400 via-fuchsia-400 to-purple-500",
  //       shimmer:
  //         "from-purple-300/60 via-violet-300/50 via-fuchsia-300/60 to-purple-400/50",
  //     },
  //     button: {
  //       primary: "bg-purple-600 hover:bg-purple-700 text-white",
  //       secondary:
  //         "text-purple-600 hover:text-purple-700 border-purple-600 hover:border-purple-700",
  //     },
  //     nav: {
  //       active: "text-purple-600",
  //       hover: "text-purple-500 hover:text-purple-700",
  //     },
  //   },
  // },
  {
    path: "/documentation",
    name: "Documentation",
    icon: "/assets/REPLENICO/page-icons/list.png",
    colors: {
      primary: "purple-600",
      secondary: "purple-700",
      gradient: {
        base: "from-purple-400 via-violet-400 via-fuchsia-400 to-purple-500",
        shimmer:
          "from-purple-300/60 via-violet-300/50 via-fuchsia-300/60 to-purple-400/50",
      },
      button: {
        primary: "bg-purple-600 hover:bg-purple-700 text-white",
        secondary:
          "text-purple-600 hover:text-purple-700 border-purple-600 hover:border-purple-700",
      },
      nav: {
        active: "text-purple-600",
        hover: "text-purple-500 hover:text-purple-700",
      },
    },
  },
  {
    path: "/blog",
    name: "Blog",
    icon: "/assets/REPLENICO/page-icons/list.png",
    colors: {
      primary: "purple-600",
      secondary: "purple-700",
      gradient: {
        base: "from-purple-400 via-violet-400 via-fuchsia-400 via-pink-400 to-purple-500",
        shimmer:
          "from-purple-300/60 via-violet-300/50 via-fuchsia-300/60 to-purple-400/50",
      },
      button: {
        primary: "bg-purple-600 hover:bg-purple-700 text-white",
        secondary:
          "text-purple-600 hover:text-purple-700 border-purple-600 hover:border-purple-700",
      },
      nav: {
        active: "text-purple-600",
        hover: "text-purple-500 hover:text-purple-700",
      },
    },
  },
  {
    path: "/legal",
    name: "Legal",
    icon: "/assets/REPLENICO/page-icons/globe.png",
    colors: {
      primary: "gray-600",
      secondary: "gray-700",
      gradient: {
        base: "from-gray-400 via-slate-400 via-zinc-400 via-neutral-400 to-gray-500",
        shimmer:
          "from-gray-300/60 via-slate-300/50 via-zinc-300/60 to-gray-400/50",
      },
      button: {
        primary: "bg-gray-600 hover:bg-gray-700 text-white",
        secondary:
          "text-gray-600 hover:text-gray-700 border-gray-600 hover:border-gray-700",
      },
      nav: {
        active: "text-gray-600",
        hover: "text-gray-500 hover:text-gray-700",
      },
    },
  },
];

// Additional navigation items
// HIDDEN ROUTE - Uncomment to show in navigation
// export const additionalNavItems = [{ title: "Pricing", href: "/pricing" }];
export const additionalNavItems: { title: string; href: string }[] = [];

// Utility functions
export function isPublicPage(pathname: string): boolean {
  return publicRoutes.some((route) => pathname?.startsWith(route.path));
}

export function isHomePage(pathname: string): boolean {
  return pathname === "/";
}

export function getPublicRouteByPath(
  pathname: string
): PublicRoute | undefined {
  return publicRoutes.find((route) => pathname?.startsWith(route.path));
}

export function getAllPublicPaths(): string[] {
  return publicRoutes.map((route) => route.path);
}

export function getPublicRouteIcon(pathname: string): string | undefined {
  const route = getPublicRouteByPath(pathname);
  return route?.icon;
}

// Get page colors from centralized navigation data
export function getPageColorsFromRoute(pathname: string) {
  const route = getPublicRouteByPath(pathname);
  return (
    route?.colors || {
      primary: "blue-600",
      secondary: "blue-700",
      gradient: {
        base: "from-blue-400 via-cyan-400 via-teal-400 via-emerald-400 to-blue-500",
        shimmer:
          "from-blue-300/60 via-cyan-300/50 via-teal-300/60 to-blue-400/50",
      },
      button: {
        primary: "bg-blue-600 hover:bg-blue-700 text-white",
        secondary:
          "text-blue-600 hover:text-blue-700 border-blue-600 hover:border-blue-700",
      },
      nav: {
        active: "text-blue-600",
        hover: "text-blue-500 hover:text-blue-700",
      },
    }
  );
}

// Tailwind color to hex mapping
const TAILWIND_COLOR_MAP: Record<string, string> = {
  "purple-600": "#a855f7",
  "purple-500": "#a855f7",
  "teal-600": "#14b8a6",
  "teal-500": "#14b8a6",
  "blue-600": "#2563eb",
  "emerald-600": "#059669",
  "indigo-600": "#4f46e5",
};

// Get hex accent color from route pathname
export function getAccentColorFromRoute(pathname: string): string {
  const colors = getPageColorsFromRoute(pathname);
  const tailwindColor = colors.primary;
  return TAILWIND_COLOR_MAP[tailwindColor] || "#a855f7"; // default to purple
}
